import { getFinancialYearLabels } from "../../brsrFinancialYearLabels.js";
import {
  BODY_FONT,
  HEADER_FILL,
  LINK_FONT,
  NA_FILL,
  THIN,
  THIN_BLACK,
  TITLE_FONT,
  VALUE_FILL,
} from "./detailSheetCommon.js";

export {
  BODY_FONT,
  HEADER_FILL,
  LINK_FONT,
  NA_FILL,
  THIN_BLACK,
  TITLE_FONT,
  VALUE_FILL,
};

/** Formula / linked cells (reference FFBEBEBE). */
export const LINKED_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFBEBEBE" },
};

/** Alias used by principle6SheetHelpers. */
export const CALC_FILL = LINKED_FILL;

export const SUBHEADER_FILL = NA_FILL;

/** Kept for otherAssessments / detailsOfOtherValueChain / principle6 legacy sheets. */
export const TABLE_HEADER_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFFCE4D6" },
};

export const COL_D = 4;
export const COL_E = 5;
export const COL_F = 6;
export const COL_G = 7;
export const COL_H = 8;
export const COL_I = 9;
export const COL_J = 10;
export const COL_K = 11;
export const COL_L = 12;
export const COL_M = 13;
export const COL_N = 14;
export const COL_END = COL_N;
export const COL_LABEL_END = COL_H;
export const COL_VALUE_START = COL_I;
export const COL_VALUE_END = COL_N;

const MEDIUM = { style: "medium", color: { argb: "FF000000" } };
const COL_COUNT = COL_N - COL_D + 1;

export function parseBorderSpec(spec) {
  const map = { t: THIN, m: MEDIUM };
  return {
    top: spec[0] !== "-" ? map[spec[0]] : undefined,
    left: spec[1] !== "-" ? map[spec[1]] : undefined,
    bottom: spec[2] !== "-" ? map[spec[2]] : undefined,
    right: spec[3] !== "-" ? map[spec[3]] : undefined,
  };
}

export function borderFromSpecs(specs, col, colStart = COL_D) {
  const spec = specs?.[col - colStart];
  return spec ? parseBorderSpec(spec) : THIN_BLACK;
}

export function applyBorderSpecs(ws, row, specs, colStart = COL_D) {
  if (!specs?.length) return;
  specs.forEach((spec, index) => {
    const cell = ws.getCell(row, colStart + index);
    cell.border = parseBorderSpec(spec);
    if (!cell.font) cell.font = BODY_FONT;
  });
}

/** Full-width D:N title band. */
export function dnTitleBorders() {
  return ["mmt-", "m-t-", "m-t-", "m-t-", "m-t-", "m-t-", "m-t-", "m-t-", "m-t-", "m-t-", "m-tm"];
}

export function dnSectionBorders() {
  return ["tmt-", "t-t-", "t-t-", "t-t-", "t-t-", "t-t-", "t-t-", "t-t-", "t-t-", "t-t-", "t-tm"];
}

export function outerMergeBorders(
  endCol,
  { first = "tmt-", middle = "t-t-", last = "t-tt", edge = "---m" } = {}
) {
  const specs = [];
  for (let col = COL_D; col <= COL_N; col += 1) {
    if (col > endCol && col < COL_N) specs.push("----");
    else if (col === COL_N) specs.push(edge);
    else if (col === COL_D) specs.push(first);
    else if (col === endCol) specs.push(last);
    else specs.push(middle);
  }
  return specs;
}

export function setCell(ws, row, col, value, options = {}) {
  const cell = ws.getCell(row, col);
  if (options.link) {
    cell.value = { text: value || "Add Details", hyperlink: options.link };
    cell.font = LINK_FONT;
  } else if (options.formula) {
    cell.value = { formula: options.formula };
    cell.font = options.font || (options.bold ? { ...BODY_FONT, bold: true } : BODY_FONT);
  } else {
    cell.value = value;
    if (options.font) {
      cell.font =
        options.bold !== undefined ? { ...options.font, bold: options.bold } : options.font;
    } else {
      cell.font = options.bold ? { ...BODY_FONT, bold: true } : BODY_FONT;
    }
  }

  if (options.border) cell.border = options.border;
  else if (options.bordered !== false) cell.border = THIN_BLACK;

  cell.alignment = {
    horizontal: options.align ?? (options.link ? "center" : "left"),
    vertical: options.vertical || "middle",
    wrapText: options.wrapText !== false,
  };
  if (options.fill) cell.fill = options.fill;
}

export function mergeRow(ws, row, startCol, endCol, value, options = {}) {
  if (startCol < endCol) ws.mergeCells(row, startCol, row, endCol);
  setCell(ws, row, startCol, value, options);
  for (let col = startCol + 1; col <= endCol; col += 1) {
    const cell = ws.getCell(row, col);
    if (options.bordered !== false && !options.border) cell.border = THIN_BLACK;
  }
}

export function padMergeRow(ws, row, startCol, endCol, specs) {
  for (let col = startCol + 1; col <= endCol; col += 1) {
    const cell = ws.getCell(row, col);
    if (cell.value === null || cell.value === undefined) cell.value = "";
  }
  if (startCol < endCol) {
    ws.mergeCells(row, startCol, row, endCol);
  }
  if (!specs?.length) return;
  const startBorder = borderFromSpecs(specs, startCol) || {};
  const endBorder = borderFromSpecs(specs, endCol) || {};
  const master = ws.getCell(row, startCol);
  master.border = {
    top: startBorder.top || endBorder.top,
    left: startBorder.left,
    bottom: startBorder.bottom || endBorder.bottom,
    right: endBorder.right || startBorder.right,
  };
}

export function mergeBlock(ws, startRow, startCol, endRow, endCol, value, options = {}) {
  if (startRow !== endRow || startCol !== endCol) {
    ws.mergeCells(startRow, startCol, endRow, endCol);
  }
  setCell(ws, startRow, startCol, value, options);
}

export function safeText(value, fallback = "") {
  if (value === null || value === undefined || value === "") return fallback;
  if (Array.isArray(value)) return value.length ? value.join(", ") : fallback;
  if (typeof value === "object") return fallback;
  return String(value);
}

export function toYesNo(value) {
  if (value === true || value === "true" || value === "yes" || value === "Yes") return "Yes";
  if (value === false || value === "false" || value === "no" || value === "No") return "No";
  if (value === "NA" || value === "N.A." || value === "na") return "NA";
  return "";
}

export function toYesNoNa(value) {
  const text = safeText(value, "");
  if (!text) return "";
  if (text.toLowerCase() === "na" || text.toLowerCase() === "n/a") return "NA";
  return toYesNo(value) || text;
}

export function formatPercent(value) {
  if (value === null || value === undefined || value === "") return "";
  const str = String(value).trim();
  if (!str) return "";
  if (str.includes("%")) return str;
  const num = Number(str);
  if (Number.isNaN(num)) return str;
  return String(num);
}

export function formatNumber(value) {
  if (value === null || value === undefined || value === "") return "";
  const num = Number(value);
  if (!Number.isNaN(num)) {
    return Number.isInteger(num) ? String(num) : String(num);
  }
  return String(value);
}

/** Convert stored % (0–100 or 0–1) to Excel ratio style used in the reference. */
export function formatRatio(value) {
  if (value === null || value === undefined || value === "") return "";
  const str = String(value).replace("%", "").trim();
  const num = Number(str);
  if (Number.isNaN(num)) return str;
  const ratio = Math.abs(num) > 1 ? num / 100 : num;
  const fixed = ratio.toFixed(4);
  return fixed.replace(/\.?0+$/, "") || "0";
}

export function setWhite(ws, row, col, value, border, align = "center") {
  setCell(ws, row, col, value ?? "", {
    align,
    fill: VALUE_FILL,
    bordered: false,
    border,
  });
}

export function setLinked(ws, row, col, value, border, align = "center") {
  setCell(ws, row, col, value ?? "", {
    align,
    fill: LINKED_FILL,
    bordered: false,
    border,
  });
}

export function setPlain(ws, row, col, value, border, align = "center") {
  setCell(ws, row, col, value ?? "", {
    align,
    bordered: false,
    border,
  });
}

export function setGrey(ws, row, col, border = parseBorderSpec("----")) {
  setCell(ws, row, col, "", {
    fill: NA_FILL,
    bordered: false,
    border,
  });
}

export function setGreyRange(ws, row, startCol, endCol = COL_N, borderSpec = "----") {
  for (let col = startCol; col <= endCol; col += 1) {
    const edge = col === COL_N ? (borderSpec === "----" ? "---m" : borderSpec) : borderSpec;
    setGrey(ws, row, col, parseBorderSpec(col === COL_N && borderSpec === "----" ? "---m" : edge));
  }
}

export function setPrinciple5ColumnWidths(ws) {
  const widths = {
    A: 9.1796875,
    B: 9.1796875,
    C: 9.1796875,
    D: 37.7265625,
    E: 15.7265625,
    F: 19.7265625,
    G: 15.7265625,
    H: 19.7265625,
    I: 19.7265625,
    J: 19.7265625,
    K: 18.54296875,
    L: 18.54296875,
    M: 18.54296875,
    N: 18.54296875,
    O: 9.1796875,
    P: 9.1796875,
    Q: 9.1796875,
  };
  Object.entries(widths).forEach(([col, width]) => {
    ws.getColumn(col).width = width;
  });
  ["A", "B"].forEach((col) => {
    ws.getColumn(col).hidden = true;
  });
  for (let col = 18; col <= 16384; col += 1) {
    ws.getColumn(col).hidden = true;
  }
}

export function setupPrinciple5SheetView(workbook, ws) {
  ws.views = [
    {
      showGridLines: false,
      state: "frozen",
      xSplit: 0,
      ySplit: 8,
      topLeftCell: "D9",
      zoomScale: 80,
      zoomScaleNormal: 80,
    },
  ];
  if (!workbook.views?.length) {
    workbook.views = [{ showGridLines: false }];
  } else {
    workbook.views[0].showGridLines = false;
  }
  for (let row = 1; row <= 5; row += 1) {
    const excelRow = ws.getRow(row);
    excelRow.hidden = true;
    excelRow.height = 0;
  }
}

export function getFyLabels(reportingYear = "") {
  const fy = getFinancialYearLabels(reportingYear);
  return {
    current: fy.currentYear ? `FY (${fy.currentYear})` : "FY (Current Financial Year)",
    previous: fy.previousYear ? `PY (${fy.previousYear})` : "PY (Previous Financial Year)",
  };
}

const LINE_HEIGHT = 15;
const MIN_ROW_HEIGHT = 18;

function estimateRowHeight(text, colWidth, minHeight = MIN_ROW_HEIGHT) {
  const str = String(text ?? "");
  if (!str.trim()) return minHeight;
  const charsPerLine = Math.max(8, Math.floor(colWidth * 0.9));
  const lineCount = str.split(/\r?\n/).reduce(
    (total, part) => total + Math.max(1, Math.ceil(part.length / charsPerLine)),
    0
  );
  return Math.max(minHeight, lineCount * LINE_HEIGHT + 8);
}

function setAutoRowHeight(ws, row, parts) {
  ws.getRow(row).height = parts.reduce(
    (max, { text, width }) => Math.max(max, estimateRowHeight(text, width)),
    MIN_ROW_HEIGHT
  );
}

/** Legacy helpers kept for principle6SheetHelpers. */
export function addSectionHeader(ws, row, title, fill = HEADER_FILL) {
  mergeRow(ws, row, COL_D, COL_END, title, { bold: true, align: "center", fill });
  return row + 1;
}

export function addQuestionRow(ws, row, label, value, options = {}) {
  if (options.fullWidth || (value === "" && !options.valueCol)) {
    mergeRow(ws, row, COL_D, COL_END, label, {
      align: "left",
      vertical: "top",
      bold: options.bold,
    });
    return row + 1;
  }
  const valueCol = options.valueCol || COL_VALUE_START;
  const valueEnd = options.valueEnd || COL_VALUE_END;
  const labelEnd = options.labelEnd || (valueCol > COL_D ? valueCol - 1 : COL_LABEL_END);
  mergeRow(ws, row, COL_D, labelEnd, label, {
    align: "left",
    vertical: "top",
    bold: options.bold,
  });
  mergeRow(ws, row, valueCol, valueEnd, value ?? "", {
    align: options.valueAlign ?? (options.link ? "center" : "left"),
    vertical: options.valueVertical || "top",
    link: options.link,
    fill: options.valueFill,
  });
  setAutoRowHeight(ws, row, [
    { text: label, width: 52 },
    { text: value, width: 58 },
  ]);
  return row + 1;
}

export function addTextAnswerRow(ws, row, label, text) {
  const value = safeText(text);
  mergeRow(ws, row, COL_D, COL_LABEL_END, label, { align: "left", vertical: "top" });
  mergeRow(ws, row, COL_VALUE_START, COL_VALUE_END, value, {
    align: "left",
    vertical: "top",
    fill: VALUE_FILL,
  });
  setAutoRowHeight(ws, row, [
    { text: label, width: 52 },
    { text: value, width: 58 },
  ]);
  return row + 1;
}

export function addShortAnswerRow(ws, row, label, value, options = {}) {
  mergeRow(ws, row, COL_D, COL_LABEL_END, label, { align: "left", vertical: "top" });
  mergeRow(ws, row, COL_VALUE_START, COL_VALUE_END, value ?? "", {
    align: options.align || "center",
    vertical: "top",
    link: options.link,
    fill: options.fill || VALUE_FILL,
  });
  setAutoRowHeight(ws, row, [{ text: label, width: 52 }]);
  return row + 1;
}

export function normalizeOtherRows(other) {
  if (!other) return [];
  if (Array.isArray(other)) {
    return other
      .map((item) => ({
        name: item?.others || item?.name || item?.assessmentName || "",
        percentage: item?.percentageValue ?? item?.percentage ?? item?.plantsOffices ?? "",
      }))
      .filter((item) => item.name || item.percentage !== "");
  }
  if (Array.isArray(other?.list)) {
    return normalizeOtherRows(other.list);
  }
  if (other?.others || other?.percentageValue) {
    return [{ name: other.others, percentage: other.percentageValue }];
  }
  return [];
}

export { COL_COUNT };
